import { useCallback, useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { isEngagementSignatureDataUrlValid } from "../utils/signatureFieldValidation";
import { useForm } from "../context/FormContext";
import { mvpApi } from "../../services/api";
import {
  isCategoryUploadComplete,
  resolveQualifyingRoute,
  shouldShowEmploymentDocCategory,
  shouldShowFamilyDocCategory,
  shouldShowCriminalRecordDocCategory,
} from "../utils/deriveDocumentChecklistHints";
import Footer from "./Footer";
import DocumentFlowHelpSheet from "./DocumentFlowHelpSheet";

interface OrganizerViewProps {
  onUploadDocs: () => void;
  onGoToStep?: (step: number) => void;
  onGoToDocCategory?: (category: string) => void;
}

export default function OrganizerView({
  onUploadDocs,
  onGoToStep,
  onGoToDocCategory,
}: OrganizerViewProps) {
  const { t, isRTL, lang } = useLang();
  const { data, caseSessionId, ensureCaseSession, update } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [helpSheetOpen, setHelpSheetOpen] = useState(false);
  const alreadySubmittedForReview = data.caseNeedsAttentionMarked === true;
  const qualifyingRoute = resolveQualifyingRoute(
    data.qualifyingGround,
    data.subRoute,
    data.irregularSubRoute,
  );
  const isAsylumApplied =
    String(data.asylumApplicant || "").trim().toLowerCase() === "yes" ||
    data.asylumRoutingConfirmed === true;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ── Intake step completion ──────────────────────────────────────────────
  const stepInfo = [
    {
      step: 1,
      labelKey: "organizer.stepConfirm",
      done: !!(
        data.registrantType &&
        data.firstName &&
        data.primerApellido &&
        data.civilStatus &&
        (data.fatherUnknown === "yes" || data.fatherFirstName) &&
        (data.motherUnknown === "yes" || data.motherFirstName) &&
        data.phone
      ),
    },
    {
      step: 2,
      labelKey: "organizer.stepQualifying",
      done:
        (data.asylumRoutingConfirmed !== null || !!data.asylumApplicant) &&
        (data.asylumRoutingConfirmed === true || !!data.qualifyingGround),
    },
    {
      step: 3,
      labelKey: "organizer.stepAddress",
      done: !!(
        data.street &&
        data.streetNumber &&
        data.city &&
        data.province &&
        data.postalCode &&
        data.country
      ),
    },
  ];

  // ── Document category completion ────────────────────────────────────────
  const categories: { key: string; labelKey: string; isDone: boolean; optional?: boolean }[] = [
    {
      key: "identity",
      labelKey: "docFlow.catIdentity",
      isDone: isCategoryUploadComplete("identity", data),
    },
    {
      key: "residence",
      labelKey: "docFlow.catResidence",
      isDone: isCategoryUploadComplete("residence", data),
    },
  ];

  if (shouldShowCriminalRecordDocCategory(data.hasCriminalRecord)) {
    categories.push({
      key: "criminal",
      labelKey: "docFlow.catCriminal",
      isDone: isCategoryUploadComplete("criminal", data),
    });
  }

  if (isAsylumApplied) {
    categories.push({
      key: "asylum",
      labelKey: "docFlow.catAsylum",
      isDone: isCategoryUploadComplete("asylum", data),
    });
  } else {
    if (shouldShowEmploymentDocCategory(qualifyingRoute)) {
      categories.push({
        key: "employment",
        labelKey: "docFlow.catEmployment",
        isDone: isCategoryUploadComplete("employment", data),
      });
    } else if (shouldShowFamilyDocCategory(qualifyingRoute)) {
      categories.push({
        key: "family",
        labelKey: "docFlow.catFamily",
        isDone: isCategoryUploadComplete("family", data),
      });
    } else if (qualifyingRoute === "vulnerability") {
      categories.push({
        key: "vulnerability",
        labelKey: "docFlow.catVulnerability",
        isDone: isCategoryUploadComplete("vulnerability", data),
      });
    }
  }

  // Optional upload bucket: visible in organizer, but must not affect "done/not done" logic.
  categories.push({
    key: "other",
    labelKey: "docFlow.catOther",
    isDone: isCategoryUploadComplete("other", data),
    optional: true,
  });

  // ── Progress calculation ────────────────────────────────────────────────
  const completedSteps = stepInfo.filter((s) => s.done).length;
  const requiredCategories = categories.filter((c) => !c.optional);
  const completedDocs = requiredCategories.filter((c) => c.isDone).length;
  const signDone = !!(
    data.engagementConsent &&
    data.dataProcessingConsent &&
    isEngagementSignatureDataUrlValid(data.signature)
  );
  const completedCount = completedSteps + completedDocs + (signDone ? 1 : 0);
  const totalCount = stepInfo.length + requiredCategories.length + 1;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const organizerHelpTitle = t("organizer.title");

  const allOtherDone =
    completedSteps === stepInfo.length &&
    completedDocs === requiredCategories.length;

  const advisorFirstName =
    String(data.advisor ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)[0] || "Ana";

  const submittedDateLabel = (() => {
    const raw = String(data.caseNeedsAttentionMarkedAt || "").trim();
    if (!raw) return "";
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return "";
    const localeByLang: Record<string, string> = {
      en: "en-US",
      es: "es-ES",
      fr: "fr-FR",
      ar: "ar",
      zh: "zh-CN",
    };
    const locale = localeByLang[lang] || "en-US";
    return new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(dt);
  })();

  const submitDoneHeadingText = submittedDateLabel
    ? t("organizer.submitDoneHeading")
        .replace("{{advisor}}", advisorFirstName)
        .replace("{{date}}", submittedDateLabel)
    : t("organizer.submitDoneHeadingNoDate").replace("{{advisor}}", advisorFirstName);

  const submitDonePara1 = t("organizer.submitDoneMessagePara1").replace(
    "{{advisor}}",
    advisorFirstName,
  );
  const submitDonePara2 = t("organizer.submitDoneMessagePara2");

  const handleSendToAdvisor = useCallback(async () => {
    if (submitting || alreadySubmittedForReview) return;
    setSubmitting(true);
    try {
      const sid = caseSessionId ?? (await ensureCaseSession());
      if (!sid) throw new Error("Missing case session id");
      const res = await mvpApi.markApplicantCaseNeedsAttention(sid);
      const markedAt = String(
        res?.data?.needs_attention_marked_at ?? res?.data?.needsAttentionMarkedAt ?? ""
      ).trim();
      update("caseNeedsAttentionMarked", true);
      update("caseNeedsAttentionMarkedAt", markedAt || new Date().toISOString());
    } catch (err) {
      console.error("Failed to mark case as needs attention", err);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, alreadySubmittedForReview, caseSessionId, ensureCaseSession, update]);

  const showReadyToSubmitBanner = allOtherDone && signDone && !alreadySubmittedForReview;

  return (
    <div
      className="min-h-screen bg-[#F8F7F4] flex flex-col"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="flex-1 flex flex-col items-center">
        <div className="w-full max-w-[480px] px-4 pt-6 pb-6">
          {/* ── Page header (organizer title + intro) ── */}
          <div className="mb-0">
            <h1 className="text-xl font-semibold text-slate-900 leading-tight mb-0.5">
              {t("organizer.title")}
            </h1>
            <p className="text-[13px] text-slate-500 leading-snug mb-4">
              {t("organizer.finishItems")}
            </p>
          </div>

          {/* ── Ready to submit banner / submitted confirmation — below organizer copy, above progress ── */}
          {alreadySubmittedForReview ? (
            <div className="rounded-2xl p-6 mb-5 bg-[#E6F4EA] border border-emerald-100/70">
              <div
                className={`flex items-start gap-3 ${isRTL ? "flex-row-reverse" : ""}`}
              >
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600"
                  aria-hidden
                >
                  <i className="ri-check-line text-white text-xl leading-none" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[17px] font-bold text-[#072711] leading-snug mb-3">
                    {submitDoneHeadingText}
                  </p>
                  <p className="text-[14px] text-[#072711]/95 leading-snug mb-3">
                    {submitDonePara1}
                  </p>
                  <p className="text-[14px] text-[#072711]/95 leading-snug">
                    {submitDonePara2}
                  </p>
                </div>
              </div>
            </div>
          ) : showReadyToSubmitBanner ? (
            <div className="rounded-2xl p-6 mb-5 bg-[#E6F4EA] border border-emerald-100/70">
              <p className="text-[17px] font-bold text-[#072711] leading-snug mb-3">
                {t("organizer.submitInlineBannerTitle")}
              </p>
              <p className="text-[14px] text-[#072711]/95 leading-snug mb-4">
                {t("organizer.submitInlineBannerBody")}
              </p>
              <button
                type="button"
                onClick={handleSendToAdvisor}
                disabled={submitting}
                className="w-full min-h-[48px] flex items-center justify-center px-4 rounded-xl font-semibold text-[15px] bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting
                  ? t("organizer.submitInlineSending")
                  : t("organizer.submitInlineBannerCta").replace(
                      "{{advisor}}",
                      advisorFirstName,
                    )}
              </button>
            </div>
          ) : null}

          {/* ── Progress ── */}
          <div className="mb-5">
            <div className="flex justify-between items-baseline mb-1.5">
              <p className="text-[12px] text-slate-500">{t("organizer.progress")}</p>
              <p className="text-[12px] text-slate-500">
                <span className="font-medium text-slate-900">{completedCount}</span>
                <span className="mx-1">{t("organizer.of")}</span>
                <span>{totalCount}</span>
              </p>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* ── Your details card ── */}
          <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-2">
            <button
              type="button"
              onClick={() => onGoToStep?.(1)}
              className="w-full flex items-center justify-between mb-2 cursor-pointer group"
            >
              <p className="text-[14px] font-medium text-slate-900 group-hover:text-emerald-700 transition-colors">
                {t("organizer.detailsLabel")}
              </p>
              <i className="ri-arrow-right-s-line text-slate-500 text-base group-hover:text-emerald-500 transition-colors" />
            </button>
            {stepInfo.map((s, i) => (
              <button
                key={s.step}
                type="button"
                onClick={() => onGoToStep?.(s.step)}
                className={`w-full flex items-center gap-2.5 py-1.5 cursor-pointer text-left ${
                  i < stepInfo.length - 1 ? "border-b border-slate-50" : ""
                }`}
              >
                <span
                  className="block w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: "#64748B" }}
                />
                <span
                  className="text-[14px] flex-1 text-slate-600"
                  style={
                    s.done
                      ? { textDecoration: "line-through", textDecorationThickness: "1.5px" }
                      : undefined
                  }
                >
                  {t(s.labelKey)}
                </span>
                <i className="ri-arrow-right-s-line text-slate-500 text-base shrink-0" />
              </button>
            ))}
          </div>

          {/* ── Documents card ── */}
          <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-2">
            <p className="text-[14px] font-medium text-slate-900 mb-2">
              {t("organizer.docsLabel")}
            </p>
            {categories.map((cat, i) => (
              <button
                key={cat.key}
                type="button"
                onClick={() => onGoToDocCategory?.(cat.key)}
                className={`w-full flex items-center gap-2.5 py-1.5 cursor-pointer text-left ${
                  i < categories.length - 1 ? "border-b border-slate-50" : ""
                }`}
              >
                <span
                  className="block w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: "#64748B" }}
                />
                <span
                  className="text-[14px] flex-1 text-slate-600"
                  style={
                    cat.isDone
                      ? { textDecoration: "line-through", textDecorationThickness: "1.5px" }
                      : undefined
                  }
                >
                  {t(cat.labelKey)}{cat.optional ? " (optional)" : ""}
                </span>
                <i className="ri-arrow-right-s-line text-slate-500 text-base shrink-0" />
              </button>
            ))}
          </div>

          {/* ── Sign & submit card ── */}
          {(() => {
            return (
              <div
                className={`bg-white rounded-2xl border border-slate-100 p-4 mb-2 ${
                  !allOtherDone ? "opacity-70" : ""
                }`}
              >
                <div className="w-full flex items-center justify-between mb-2">
                  <p className="text-[14px] font-medium text-slate-900">
                    {t("organizer.signSubmitLabel")}
                  </p>
                  {!allOtherDone && (
                    <span className="text-[11px] text-slate-400">
                      {t("organizer.signAvailableLater")}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => allOtherDone && onGoToStep?.(4)}
                  disabled={!allOtherDone}
                  className={`w-full flex items-center gap-2.5 py-1.5 text-left ${
                    signDone ? "opacity-60" : ""
                  } ${allOtherDone ? "cursor-pointer" : "cursor-not-allowed"}`}
                >
                  {signDone ? (
                    <span
                      className="block w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: "#64748B" }}
                    />
                  ) : allOtherDone ? (
                    <span
                      className="block w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: "#64748B" }}
                    />
                  ) : (
                    <i className="ri-lock-line text-slate-300 text-xl shrink-0" />
                  )}
                  <span
                    className="text-[14px] flex-1 text-slate-600"
                    style={
                      signDone
                        ? { textDecoration: "line-through", textDecorationThickness: "1.5px" }
                        : undefined
                    }
                  >
                    {t(signDone ? "organizer.signSubmitStatusComplete" : "organizer.signSubmitStatusPending")}
                  </span>
                  {!signDone && allOtherDone && (
                    <i className="ri-arrow-right-s-line text-slate-700 text-base" />
                  )}
                </button>
              </div>
            );
          })()}

        </div>
      </div>

      <DocumentFlowHelpSheet
        open={helpSheetOpen}
        onClose={() => setHelpSheetOpen(false)}
        pageTitle={organizerHelpTitle}
      />
      {!helpSheetOpen ? (
        <button
          type="button"
          onClick={() => setHelpSheetOpen(true)}
          className={`fixed z-[65] bottom-[max(1.5rem,env(safe-area-inset-bottom))] flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600 text-[26px] font-bold leading-none text-white shadow-lg shadow-emerald-900/20 border border-emerald-700/30 hover:bg-emerald-700 cursor-pointer transition-colors ${
            isRTL ? "left-[max(1.5rem,env(safe-area-inset-left))]" : "right-[max(1.5rem,env(safe-area-inset-right))]"
          }`}
          aria-label={t("docFlow.helpFabAria")}
        >
          ?
        </button>
      ) : null}

      <Footer />
    </div>
  );
}
